import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Drawer,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { AppColors } from '../../../../util/AppColors';
import { useDispatch, useSelector } from 'react-redux';
import CustomModal from '../../../../components/CustomModal/CustomModal';
import CustomButton from '../../../../components/CustomButton';
import {
  getProductList,
  createProduct,
  updateProduct,
  deleteProduct
} from '../../../../redux/reducers/productSlice';

function Products() {
  const dispatch = useDispatch();

  const [search, setSearch] = useState('');
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [form, setForm] = useState({ category: '', subCategory: '' });
  const [delModVisible, setDelModVisible] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const productState = useSelector((state) => state.product);
  const productList = productState.list;

  useEffect(() => {
    dispatch(getProductList());
  }, [dispatch]);

  const handleAddOrUpdate = () => {
    if (!form.category || !form.subCategory) return;

    if (editIndex !== null) {
      const product = productList[editIndex];
      dispatch(updateProduct({ ...form, id: product.id }));
    } else {
      dispatch(createProduct(form));
    }

    handleCloseDrawer();
  };

  const handleEdit = (index) => {
    const product = productList[index];
    setForm({
      category: product.category,
      subCategory: product.subCategory
    });
    setEditIndex(index);
    setOpenDrawer(true);
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDelModVisible(true);
  };

  const handleDelConfirm = () => {
    dispatch(deleteProduct({ id: deleteId })).then(() => {
      setDelModVisible(false);
      setDeleteId(null);
    });
  };

  const handleDelModClose = () => {
    setDelModVisible(false);
    setDeleteId(null);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setForm({ category: '', subCategory: '' });
    setEditIndex(null);
  };

  const filteredRows = productList.filter((row) =>
    row.category.toLowerCase().includes(search.toLowerCase()) ||
    row.subCategory.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} flexWrap="wrap">
        <Box display="flex" gap={2} mx="auto" mb={2}>
          <TextField
            label="Search Product"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ width: '500px', backgroundColor: AppColors.lightPink }}
          />
          <Button variant="contained" onClick={() => setOpenDrawer(true)}>
            Create
          </Button>
        </Box>
        <Typography variant="h6" sx={{ ml: 'auto' }}>
          Total Products: {filteredRows.length}
        </Typography>
      </Box>

      <Box sx={{ mt: 2, maxHeight: '60vh', overflowY: 'auto' }}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>S.No</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Sub-Category</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">No records found</TableCell>
                </TableRow>
              ) : (
                filteredRows.map((row, index) => (
                  <TableRow key={row.id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{row.category}</TableCell>
                    <TableCell>{row.subCategory}</TableCell>
                    <TableCell>{row.userName || "-"}</TableCell>
                    <TableCell>{new Date(row.createdDate).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <IconButton color="primary" size="small" onClick={() => handleEdit(index)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" size="small" onClick={() => handleDeleteClick(row.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Drawer anchor="right" open={openDrawer} onClose={handleCloseDrawer}>
        <Box p={3} width={300}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" color="primary">{editIndex !== null ? "Edit Product" : "Create Product"}</Typography>
            <IconButton onClick={handleCloseDrawer} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <TextField
            fullWidth
            select
            label="Category"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            margin="normal"
            InputLabelProps={{
              sx: {
                position: 'absolute',
                top: '50%',
                left: '14px',
                transform: 'translateY(-50%)',
                transition: 'all 0.2s ease-out',
                '&.Mui-focused, &.MuiFormLabel-filled': {
                  top: '0px',
                  transform: 'translateY(-50%) scale(0.75)',
                  transformOrigin: 'top left',
                }
              }
            }}
          >
            <MenuItem value="Grooming">Grooming</MenuItem>
            <MenuItem value="Grooming2">Grooming2</MenuItem>
          </TextField>
          <TextField
            fullWidth
            label="Sub-Category"
            value={form.subCategory}
            onChange={(e) => setForm({ ...form, subCategory: e.target.value })}
            margin="normal"
            InputLabelProps={{
              sx: {
                position: 'absolute',
                top: '50%',
                left: '14px',
                transform: 'translateY(-50%)',
                transition: 'all 0.2s ease-out',
                '&.Mui-focused, &.MuiFormLabel-filled': {
                  top: '0px',
                  transform: 'translateY(-50%) scale(0.75)',
                  transformOrigin: 'top left',
                }
              }
            }}
          />
          <Button
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            onClick={handleAddOrUpdate}
          >
            {editIndex !== null ? "Update" : "Add"}
          </Button>
        </Box>
      </Drawer>

      <CustomModal
        open={delModVisible}
        onClose={handleDelModClose}
        header="Delete"
        modal
        modalWidth={30}
      >
        <Typography className="flex-center txt-regular fs14">
          Are you sure you want to delete this record?
        </Typography>
        <div className="flex-center mb10 mt15">
          <div>
            <CustomButton text="Cancel" onClick={handleDelModClose} />
          </div>
          <div className="ml10">
            <CustomButton redBtn text="Confirm" onClick={handleDelConfirm} />
          </div>
        </div>
      </CustomModal>
    </Box>
  );
}

export default Products;
